import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { InsightBar } from '../../components/charts/InsightBar.jsx';
import { useChartPalette } from '../../components/charts/useChartPalette.js';
import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { CsvExportButton } from '../../components/data/CsvExportButton.jsx';
import { CsvImportModal } from '../../components/data/CsvImportModal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';

import { SupplierCreateModal } from './SupplierCreateModal.jsx';

const CSV_COLUMNS = [
  { key: 'code', label: 'Code' },
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
];

const CSV_FIELDS = [
  { field: 'code', label: 'Code', aliases: ['lieferantennummer', 'nr'], required: true },
  { field: 'name', label: 'Name', aliases: ['lieferant', 'firma'], required: true },
  { field: 'email', label: 'Email', aliases: ['e-mail', 'mail'] },
  { field: 'phone', label: 'Phone', aliases: ['telefon', 'tel'] },
  { field: 'address', label: 'Address', aliases: ['adresse'] },
];

export function SuppliersPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['inventory', 'suppliers'],
    queryFn: () => inventoryApi.listSuppliers().then((r) => r.items || []),
  });

  const palette = useChartPalette();
  const rows = data || [];
  const stats = useMemo(() => {
    const active = rows.filter((r) => r.isActive).length;
    return {
      active,
      inactive: rows.length - active,
      withEmail: rows.filter((r) => r.email).length,
    };
  }, [rows]);

  const columns = [
    { key: 'code',  label: t('col.code') },
    { key: 'name',  label: t('col.name') },
    { key: 'email', label: t('col.email') },
    { key: 'phone', label: t('inv.suppliers.modal.phone') },
    { key: 'isActive', label: t('col.status'), render: (r) => <Badge status={r.isActive ? 'ACTIVE' : 'INACTIVE'} /> },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('inv.suppliers.title')}</h1>
          <p className="muted">{t('inv.suppliers.subtitle')}</p>
        </div>
        <div className="page-actions">
          <CsvExportButton rows={data || []} columns={CSV_COLUMNS} filename="lieferanten" />
          <Button variant="default" onClick={() => setImportOpen(true)}>↑ {t('csv.import')}</Button>
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            {t('inv.suppliers.action.new')}
          </Button>
        </div>
      </header>
      {rows.length > 0 && (
        <InsightBar
          title={t('insight.byStatus')}
          labels={[t('status.ACTIVE'), t('status.INACTIVE')]}
          values={[stats.active, stats.inactive]}
          colors={[palette?.series?.[1], palette?.axis]}
          metrics={[
            { label: t('insight.count'), value: rows.length },
            { label: t('insight.active'), value: stats.active },
            { label: t('inv.suppliers.insight.withEmail'), value: stats.withEmail },
            { label: t('insight.inactive'), value: stats.inactive },
          ]}
        />
      )}
      {error && <div className="alert danger">{error.message}</div>}
      <Table columns={columns} rows={data} isLoading={isLoading} emptyMessage={t('empty.default')} />
      <SupplierCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <CsvImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title={t('inv.suppliers.action.new')}
        fields={CSV_FIELDS}
        onImportRow={(row) =>
          inventoryApi.createSupplier({
            code: row.code,
            name: row.name,
            email: row.email || undefined,
            phone: row.phone || undefined,
            address: row.address || undefined,
          })
        }
        onDone={() => queryClient.invalidateQueries({ queryKey: ['inventory', 'suppliers'] })}
      />
    </section>
  );
}
